/**
 * SERVICE-FIELDS — методы обновления полей у уже добавленных услуг
 * (только secition `services` — для отелей/перелётов/прочего есть свои handlers).
 *
 * Phase 5 sub-extract (Sprint 15 C.3 part 1, 2026-05-10).
 * Извлечено из public/js/app.js.
 *
 * Содержит:
 *   • handleEdit — inline-edit по data-field (используется в editable cells)
 *   • updateServiceRegion / updateServiceField / changeQuantity
 *   • toggle* — noDayAssignment, exclusion, fullProfit, bookingStatus, paymentStatus
 *   • moveServiceToOther — выкинуть из основных в "прочие"
 *
 * Все методы оперируют ТОЛЬКО `this.state.services`. Для прочих секций
 * используются inline-handlers в render-sections.js.
 *
 * Подключение из entry.js через `attachServiceFields(ProfessionalQuoteCalculator)`.
 *
 * @module app/service-fields
 */

'use strict';

// i18n helper: returns translation if loaded, else fallback (EN). Safe to call before module loads.
const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

export function attachServiceFields(ClassRef) {
    Object.assign(ClassRef.prototype, {
        handleEdit(element) {
            const field = element.dataset.field;
            const serviceId = element.dataset.id;
            const value = element.textContent.trim();

            if (!serviceId) return;

            const service = this.state.services.find(s => s.id === serviceId);
            if (!service) return;

            service[field] = value;

            // Validate change
            const errors = this.validateService(service);
            if (errors.length > 0) {
                this.showNotification(errors[0], true);
                element.classList.add('error');
            } else {
                element.classList.remove('error');
            }

            this.scheduleUpdate();
        },

        updateServiceRegion(serviceId, newRegion) {
            const service = this.state.services.find(s => s.id === serviceId);
            if (!service) {
                this.showNotification(_t('app.service_fields.service_not_found', 'Service not found'), true);
                return;
            }

            const oldRegion = service.region;
            service.region = newRegion;

            this.renderServices();
            this.scheduleUpdate();
            this.markQuoteAsUnsaved();

            this.showNotification(
                _t('app.service_fields.region_changed', 'Region changed: {old} → {new}')
                    .replace('{old}', oldRegion).replace('{new}', newRegion)
            );
        },

        updateServiceField(serviceId, field, value) {
            console.log(`[CHECKLIST] updateServiceField called: ${field}, value: ${value}, serviceId: ${serviceId}`);
            const service = this.state.services.find(s => s.id === serviceId);
            if (!service) return;

            if (field === 'day' || field === 'quantity') {
                service[field] = Math.max(1, parseInt(value) || 1);
            } else if (field === 'price' || field === 'markup') {
                service[field] = Math.max(0, parseFloat(value) || 0);
            } else {
                service[field] = value;
            }

            // Update duration display if time fields changed
            // НЕ перерисовываем для полей времени - только обновляем duration
            if (field === 'startTime' || field === 'endTime') {
                console.log(`[CHECKLIST] Time field detected - updating duration ONLY, NO RERENDER`);
                const durationElement = document.getElementById(`duration-${serviceId}`);
                if (durationElement) {
                    durationElement.textContent = this.calculateDuration(service.startTime, service.endTime);
                    console.log(`[CHECKLIST] Duration updated to: ${durationElement.textContent}`);
                }
                // Только обновляем расчёты, НЕ перерисовываем
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();
                console.log(`[CHECKLIST] Time field change complete - NO RERENDER called`);
                return; // ВАЖНО: ранний выход, не вызываем renderServices()
            }

            // Validate
            const errors = this.validateService(service);
            if (errors.length > 0) {
                this.showNotification(errors[0], true);
            }

            console.log(`[CHECKLIST] Non-time field - calling RERENDER`);
            this.renderServices();
            this.scheduleUpdate();
            this.markQuoteAsUnsaved();
        },

        changeQuantity(serviceId, delta) {
            const service = this.state.services.find(s => s.id === serviceId);
            if (service) {
                service.quantity = Math.max(1, service.quantity + delta);
                this.renderServices();
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();
            }
        },

        toggleServiceNoDayAssignment(serviceId) {
            const service = this.state.services.find(s => s.id === serviceId);

            if (service) {
                service.noDayAssignment = !service.noDayAssignment;

                this.renderServices();
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();

                const message = service.noDayAssignment ?
                    '📌 ' + _t('app.service_fields.no_day_on', 'Service not bound to days (flights, visas, etc.)') :
                    _t('app.service_fields.no_day_off', 'Service bound to days');
                this.showNotification(message);
            }
        },

        toggleServiceExclusion(serviceId) {
            const service = this.state.services.find(s => s.id === serviceId);

            if (service) {
                service.excludeFromMarkup = !service.excludeFromMarkup;

                this.renderServices();
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();

                const message = service.excludeFromMarkup ?
                    _t('app.service_fields.exclude_on', 'Service excluded from total profit') :
                    _t('app.service_fields.exclude_off', 'Service included in total profit');
                this.showNotification(message);
            } else {
                console.error('Service not found with id:', serviceId);
            }
        },

        toggleServiceFullProfit(serviceId) {
            const service = this.state.services.find(s => s.id === serviceId);

            if (service) {
                service.fullProfit = !service.fullProfit;

                this.renderServices();
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();

                const message = service.fullProfit ?
                    _t('app.service_fields.full_profit_on', 'Whole service amount goes to profit (cost = 0)') :
                    _t('app.service_fields.full_profit_off', 'Service in normal calculation mode');
                this.showNotification(message);
            } else {
                console.error('Service not found with id:', serviceId);
            }
        },

        moveServiceToOther(serviceId, isChecked) {
            if (!isChecked) {
                // Чекбокс снят - ничего не делаем (нельзя вернуть обратно)
                this.showNotification(_t('app.service_fields.use_drag_to_restore', 'Use drag and drop to restore the service'), true);
                return;
            }

            const service = this.state.services.find(s => s.id === serviceId);

            if (service) {
                // Удаляем из основных услуг
                this.state.services = this.state.services.filter(s => s.id !== serviceId);

                // Удаляем из выбранных если была выбрана
                this.state.selectedServices.delete(serviceId);

                // Добавляем в прочие услуги
                this.state.otherServices.unshift(service);

                // Перерендериваем обе секции
                this.renderServices();
                this.renderOtherServices();
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();

                this.showNotification(
                    _t('app.service_fields.moved_to_other', 'Service "{name}" moved to Other')
                        .replace('{name}', service.name)
                );
            } else {
                console.error('Service not found with id:', serviceId);
            }
        },

        toggleBookingStatus(serviceId) {
            // Phase 8a: ищем сервис во всех секциях (services / hotels / flights / otherServices),
            // т.к. status-icons теперь живут в карточках всех секций cards-mode.
            const service = this.state.services.find(s => s.id === serviceId);

            if (service) {
                service.done = !service.done;

                this.renderServices();
                this.renderChecklist(); // Синхронизируем с чеклистом
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();

                const message = service.done ?
                    '✅ ' + _t('app.service_fields.booked', 'Service booked') :
                    '⬜ ' + _t('app.service_fields.booking_cancelled', 'Booking cancelled');
                this.showNotification(message);
            }
        },

        togglePaymentStatus(serviceId) {
            const service = this.state.services.find(s => s.id === serviceId);

            if (service) {
                service.paid = !service.paid;

                this.renderServices();
                this.renderChecklist(); // Синхронизируем с чеклистом
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();

                const message = service.paid ?
                    '💵 ' + _t('app.service_fields.paid', 'Service paid') :
                    '💰 ' + _t('app.service_fields.payment_unconfirmed', 'Payment unconfirmed');
                this.showNotification(message);
            }
        }
    });
}
